import {
  Box,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Shape } from "../RampCanvasV2/RampCanvasV2";
import { PropertiesTab } from "../RampCanvasV2/ShapeEditDialog/tabs/PropertiesTab";
import { StylingTab } from "../RampCanvasV2/ShapeEditDialog/tabs/StylingTab";
import { AlgorithmTab } from "../RampCanvasV2/ShapeEditDialog/tabs/AlgorithmTab";

type Props = {
  shapes: Shape[];
  setShapes: (shapes: Shape[]) => void;
  activeShapes: string[];
  canvasWidth?: number;
};

/**
 * Properties section for LayerPanel - shows shape properties when shapes are selected
 * Matches Figma-style right sidebar pattern
 */
export const PropertiesSection: React.FC<Props> = ({
  shapes,
  setShapes,
  activeShapes,
  canvasWidth = 0,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState(0);

  // Get the primary selected shape (first in selection)
  const primaryShape = React.useMemo(
    () => shapes.find((s) => activeShapes.includes(s.id)),
    [shapes, activeShapes]
  );

  // Get all selected shapes
  const selectedShapes = React.useMemo(
    () => shapes.filter((s) => activeShapes.includes(s.id)),
    [shapes, activeShapes]
  );

  // Check if a property has mixed values across selected shapes
  const hasMixedValue = React.useCallback(
    (key: keyof Shape) => {
      if (selectedShapes.length <= 1) return false;
      const firstValue = selectedShapes[0]?.[key];
      return selectedShapes.some((s) => s[key] !== firstValue);
    },
    [selectedShapes]
  );

  // Check if selected shapes have mixed types
  const hasMixedTypes = React.useMemo(() => {
    if (selectedShapes.length <= 1) return false;
    const firstType = selectedShapes[0]?.type;
    return selectedShapes.some((s) => s.type !== firstType);
  }, [selectedShapes]);

  // Set a single property on all selected shapes
  const setSelectedShapes = React.useCallback(
    (attribute: string, value: unknown) => {
      const updatedShapes = shapes.map((shape) => {
        if (activeShapes.includes(shape.id)) {
          return { ...shape, [attribute]: value };
        }
        return shape;
      });
      setShapes(updatedShapes);
    },
    [shapes, activeShapes, setShapes]
  );

  // Set multiple properties on all selected shapes
  const setSelectedShapesMultiple = React.useCallback(
    (attributes: Record<string, unknown>) => {
      const updatedShapes = shapes.map((shape) => {
        if (activeShapes.includes(shape.id)) {
          return { ...shape, ...attributes };
        }
        return shape;
      });
      setShapes(updatedShapes);
    },
    [shapes, activeShapes, setShapes]
  );

  // Determine if Algorithm tab should be shown
  const showAlgorithmTab = React.useMemo(() => {
    if (!primaryShape || hasMixedTypes) return false;
    return (
      primaryShape?.tags?.includes("parkable") ||
      primaryShape?.tags?.includes("reference_line") ||
      primaryShape?.tags?.includes("spot") ||
      primaryShape?.type === "Spot" ||
      primaryShape?.type === "Zone"
    );
  }, [primaryShape, hasMixedTypes]);

  // Reset tab if out of bounds
  React.useEffect(() => {
    const totalTabs = showAlgorithmTab ? 3 : 2;
    if (activeTab >= totalTabs) {
      setActiveTab(totalTabs - 1);
    }
  }, [showAlgorithmTab, activeTab]);

  // Don't render if no shapes selected
  if (activeShapes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Selected layer indicator */}
      {selectedShapes.length > 0 && (
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {selectedShapes.length === 1
              ? `Editing: ${primaryShape?.display_name || primaryShape?.type || "Shape"}`
              : `${selectedShapes.length} shapes selected`}
          </Typography>
        </Box>
      )}

      {/* Property type tabs (Props/Style/Algo) */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ minHeight: 32 }}
        >
          <Tab
            label={t("settings.rampsSettings.rampEditor.shapeEdit.tabs.properties", "Props")}
            sx={{ minHeight: 32, py: 0, fontSize: "0.75rem", textTransform: "none" }}
          />
          <Tab
            label={t("settings.rampsSettings.rampEditor.shapeEdit.tabs.styling", "Style")}
            sx={{ minHeight: 32, py: 0, fontSize: "0.75rem", textTransform: "none" }}
          />
          {showAlgorithmTab && (
            <Tab
              label={t("settings.rampsSettings.rampEditor.shapeEdit.tabs.algorithm", "Algo")}
              sx={{ minHeight: 32, py: 0, fontSize: "0.75rem", textTransform: "none" }}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1, py: 0.5, minHeight: 0 }}>
        <PropertiesTab
          value={activeTab}
          index={0}
          shape={primaryShape}
          hasMixedTypes={hasMixedTypes}
          hasMixedValue={hasMixedValue}
          setSelectedShapes={setSelectedShapes}
        />
        <StylingTab
          value={activeTab}
          index={1}
          shape={primaryShape}
          shapes={shapes}
          activeShapes={activeShapes}
          contextMenu={null}
          setShapes={setShapes}
          setSelectedShapes={setSelectedShapes}
          setSelectedShapesMultiple={setSelectedShapesMultiple}
        />
        {showAlgorithmTab && (
          <AlgorithmTab
            value={activeTab}
            index={2}
            shape={primaryShape}
            shapes={shapes}
            activeShapes={activeShapes}
            contextMenu={null}
            setShapes={setShapes}
            setSelectedShapes={setSelectedShapes}
            canvasWidth={canvasWidth}
            previewSpots={[]}
            setPreviewSpots={() => {}}
          />
        )}
      </Box>
    </Box>
  );
};
